import { useEffect, useState } from 'react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { DocsIcon, StructuredMetadataTable } from '@backstage/core-components';
import {
  alertApiRef,
  configApiRef,
  errorApiRef,
  identityApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { WorkloadClient } from '@internal/workload-api-helper';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { SeverityEnum } from '../../../enums/SeverityEnum';
import { getMessage } from '../../../utils/getMessage';
import { BudgetCardProps, BudgetFormFieldName, Features } from '../types';
import { isValueDefined, objectIsEmpty } from '../utils/checks';
import { updateResource } from '../utils/patching';
import { BudgetEditIcon } from './BudgetEditIcon';
import { AwsBudgetHealthGrid } from '@internal/plugin-aws-finops-frontend';
import { useStyles } from '../styles';
import { useWorkload } from '../hooks/useWorkload';
import { useAccountId } from '../hooks/useAccountId';

const updatePayload = ({
  amount,
}: {
  amount?: string;
}): { [key: string]: string } => {
  const payload: { [key: string]: string } = {};

  if (amount) {
    payload.amount = amount;
  }

  return payload;
};

const parseWorkloadObjectValues = (
  catalogLocationStringList: string[] | undefined,
): { [key: string]: string | undefined } => {
  const resourceFileName: string | undefined = catalogLocationStringList?.pop();
  const resourceName = resourceFileName?.split('.')[0];
  const workload = catalogLocationStringList?.pop();

  return {
    resourceName,
    workload,
  };
};

export function BudgetCard(props: BudgetCardProps) {
  const [requesterEmail, setRequesterEmail] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);
  const identity = useApi(identityApiRef);
  const config = useApi(configApiRef);
  const catalogApi = useApi(catalogApiRef);

  // Use the existing hooks that properly handle entity changes
  const { workload: workloadData, loading: loadingRequiredEditData } =
    useWorkload();
  const accountId = useAccountId();

  const oauthApi = useApi(microsoftAuthApiRef);
  const workloadClient = new WorkloadClient(config, oauthApi);

  const { entity } = useEntity();
  const features = entity?.spec?.features as unknown as Features;

  // Locally-tracked budget amount so the card reflects the confirmed value
  // immediately, before the entity/catalog refresh completes.
  const [displayedAmount, setDisplayedAmount] = useState<string | undefined>(
    features?.budget?.amount,
  );

  useEffect(() => {
    setDisplayedAmount(features?.budget?.amount);
  }, [features?.budget?.amount]);

  useEffect(() => {
    async function getRequesterEmail() {
      const profile = await identity.getProfileInfo();
      setRequesterEmail(profile.email || '');
    }
    getRequesterEmail();
  }, [identity]);

  const FormFieldEnum: BudgetFormFieldName = Object.freeze({
    amount: 'budget',
  });

  const formFields = [...Object.values(FormFieldEnum)];

  const formContext = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldUnregister: false,
    defaultValues: {
      [FormFieldEnum.amount]: String(features?.budget?.amount),
    },
  });

  // Reset form when entity/features change
  useEffect(() => {
    formContext.reset({
      [FormFieldEnum.amount]: String(features?.budget?.amount),
    });
  }, [entity, features?.budget?.amount, formContext, FormFieldEnum.amount]);

  // Close modal when account changes to prevent stale data display
  useEffect(() => {
    setIsModalOpen(false);
  }, [accountId]);

  const apiValidationPayload = {
    form: formContext,
    identity: identity,
    config: config,
  };

  const { variant } = props;
  const classes = useStyles();
  let cardClass = '';
  let cardContentClass = '';
  if (variant === 'gridItem') {
    cardClass = classes.gridItemCard;
    cardContentClass = classes.gridItemCardContent;
  } else if (variant === 'fullHeight') {
    cardClass = classes.fullHeightCard;
    cardContentClass = classes.fullHeightCardContent;
  }

  const handleClickOpen = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const stopLoadingAndCloseModal = () => {
    setIsModalOpen(false);
    setIsLoading(false);
  };

  const killProcessAndReportError = (e: Error) => {
    stopLoadingAndCloseModal();
    errorApi.post(e);
  };

  const isFormValid = async () => {
    let response = false;

    await Promise.all(
      formFields.map(fieldName => formContext.trigger(fieldName)),
    );

    if (Object.keys(formContext.formState.errors).length === 0) {
      response = true;
    }

    return response;
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (!(await isFormValid())) {
      return Promise.resolve();
    }

    const formValues: { [key: string]: string | undefined } =
      apiValidationPayload.form.getValues();
    const payload: { [key: string]: string } = updatePayload({
      amount: formValues[FormFieldEnum.amount],
    });

    if (objectIsEmpty(payload)) {
      stopLoadingAndCloseModal();
      return Promise.resolve();
    }

    let workloadObject: { [key: string]: string | undefined } = {};

    const stripCatalogLocation = workloadData?.catalogLocation;
    const catalogLocationStringList = stripCatalogLocation?.split('/');
    if (workloadData && catalogLocationStringList) {
      workloadObject = parseWorkloadObjectValues(catalogLocationStringList);
    } else {
      stopLoadingAndCloseModal();
      alertApi.post({
        message: getMessage({
          failureToDo: 'find your resource with the data provided',
        }).FAILURE,
        severity: SeverityEnum.ERROR,
      });
      return Promise.resolve();
    }

    if (workloadData && isValueDefined(workloadObject?.workload)) {
      const updateSucceeded = await updateResource({
        workloadClient,
        workloadObject,
        payload,
        stopLoadingAndCloseModal,
        alertApi,
        killProcessAndReportError,
      });
      if (updateSucceeded && payload.amount) {
        setDisplayedAmount(payload.amount);
        await catalogApi.refreshEntity(stringifyEntityRef(entity));
        alertApi.post({
          message: getMessage().REFRESHING,
          severity: SeverityEnum.INFO,
        });
      }
    } else {
      stopLoadingAndCloseModal();
      alertApi.post({
        message: getMessage({ failureToDo: 'retrieve workload' }).FAILURE,
        severity: SeverityEnum.WARNING,
      });
    }
    stopLoadingAndCloseModal();
    return Promise.resolve();
  };

  return (
    <Card className={cardClass}>
      <CardHeader
        title="Budget"
        titleTypographyProps={{ component: 'h2' }}
        action={
          <CardActions>
            <Tooltip title="Learn more about budgets in AWS">
              <IconButton
                href="https://portal.developer.principal.com/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/design/finops/tools/aws-budgeting/"
                target="_blank"
              >
                <DocsIcon />
              </IconButton>
            </Tooltip>
            <BudgetEditIcon
              apiValidationPayload={apiValidationPayload}
              cardClass={cardClass}
              cardContentClass={cardContentClass}
              features={features}
              handleClickOpen={handleClickOpen}
              handleClose={handleClose}
              handleConfirm={handleConfirm}
              disableConfirm={!formContext.formState.isDirty}
              isLoading={isLoading}
              isLoadingEdit={loadingRequiredEditData}
              isModalOpen={isModalOpen}
              requesterEmail={requesterEmail}
              workloadData={workloadData}
            />
          </CardActions>
        }
      />
      <Divider />
      <CardContent className={cardContentClass}>
        <StructuredMetadataTable
          metadata={{
            'Budget Amount': isValueDefined(displayedAmount)
              ? `$${displayedAmount}`
              : 'Unknown',
            'Notification Email Address':
              features?.budget?.notification_email || 'Unknown',
          }}
        />
        <Divider style={{ marginTop: '10px', marginBottom: '10px' }} />
        <AwsBudgetHealthGrid accountId={accountId} budgetName="PFG Budget" />
      </CardContent>
    </Card>
  );
}
